import { twAuthClient } from "@teamworksdev/web-sdk/auth";
import { AskTeamworksBottomBar, AskTeamworksProvider, FloatingChatSurface, useAskTeamworksContext } from "@teamworksdev/ai-react";
import "@teamworksdev/ai-react/styles.css";
import { useCallback } from "react";
import { environment } from "../../environment";
import { useTeamworksAuthContext } from "../../contexts/TeamworksAuth";
import { useUser } from "../../utils/tokens";

// Per-environment via VITE_ASK_TEAMWORKS_API_URL (apps/api runs unproxied on :3300 in dev).
const ASK_TEAMWORKS_API_URL = environment.VITE_ASK_TEAMWORKS_API_URL;

// Scopes the widget to only the nutrition product agent (and, transitively, only
// the mcp app's tools) -- see apps/api's PRODUCT_AGENT_REGISTRY in ask-teamworks-app.
const NUTRITION_AGENT_ID = "nutrition";

const AskTeamworksFloatingShell = () => {
  const { chatMode } = useAskTeamworksContext();
  return chatMode === "floating" ? <FloatingChatSurface /> : null;
};

/**
 * Floating Ask Teamworks trigger + panel for the Menu Order Analytics dashboard.
 *
 * Renders nothing until the Unity auth client (shared with the AMS/messaging MFE
 * integrations) has a token -- orgs that aren't axle-synced never get one, so the
 * widget stays hidden for them rather than showing a trigger that can never authenticate.
 */
export const AskTeamworksWidget = () => {
  const { isAuthReady } = useTeamworksAuthContext();
  // The Unity token's `sub` is per org membership, and Ask Teamworks only lets a token's owner modify its
  // conversations. Scoping by membership starts a fresh conversation on org switch instead of a 403.
  const authScope = useUser()?.orgMembership?.axlePersonDocumentId ?? undefined;

  const getToken = useCallback(async () => (await twAuthClient.ensureToken()) ?? "", []);

  if (!isAuthReady) {
    return null;
  }

  return (
    <AskTeamworksProvider
      apiUrl={ASK_TEAMWORKS_API_URL}
      getToken={getToken}
      authScope={authScope}
      lockedAgentId={NUTRITION_AGENT_ID}
      greeting={{ title: "Ask about menu orders", subtitle: "Ask about menu item performance, ingredient usage, and order trends." }}
      placeholder="Ask about menu orders..."
    >
      <AskTeamworksFloatingShell />
      <AskTeamworksBottomBar />
    </AskTeamworksProvider>
  );
};
